import React, { FC } from 'react';
import styled from 'styled-components';
import { useQuery } from '@apollo/react-hooks';
import { useTranslation } from 'Shared/Hooks/useTranslation';

import Avatar from 'crehana-ui/Avatar';
import Star from 'crehana-icons/Star';
import CrownFilled from 'crehana-icons/CrownFilled';
import { yellowMain } from 'crehana-ui/styles/colors';

import { humanize } from 'Jsx/Utils/date';
import StringParser from 'Jsx/Shared/StringParser';
import locales from '../../i18n/localeEs';

import GET_FEATURED_REVIEW_QUERY from './graphql/getFeaturedReview.graphql';

const translations = locales.es;

const CrownWrapper = styled.div`
  left: 24px;
  top: -24px;
`;

type Node = {
  node: Review;
};

type Review = {
  originalId: number;
  comment: string;
  dateCreated: string;
  score: number;
  user: {
    fullName: string;
    profileUrl: string;
    username: string;
    thumbnailUrl: string;
  };
};

type FeaturedReviewProps = {
  courseId: number;
  setFeaturedReviewId(featuredReviewId: number): void;
};

const FeaturedReview: FC<FeaturedReviewProps> = ({
  courseId,
  setFeaturedReviewId,
}) => {
  const { t } = useTranslation();
  const [featuredReview, setFeaturedReview] = React.useState<Review | null>(
    null,
  );
  const { data, loading, error } = useQuery<
    {
      reviews: {
        edges: Node[];
      };
    },
    { id: number; first: number }
  >(GET_FEATURED_REVIEW_QUERY, {
    variables: { id: courseId, first: 1 },
  });

  React.useEffect(() => {
    if (data && !loading && !error) {
      const _reviews = data?.reviews?.edges || [];
      const _featuredReview =
        _reviews.length > 0 && _reviews.map(el => el.node)[0];

      if (_featuredReview) {
        setFeaturedReview(_featuredReview);
        setFeaturedReviewId(_featuredReview.originalId);
      }
    }
  }, [data, loading, error]);

  if (!featuredReview) {
    return null;
  }

  return (
    <article className="mb-40 sm:mb-48">
      <h4 className="mb-40 sm:mb-48 font-h4">
        {t('FEATURED_REVIEW_TITLE', translations.FEATURED_REVIEW_TITLE)}
      </h4>
      <div className="p-24 relative bg-gray-lighter">
        <div className="flex mb-16 items-center">
          <Avatar
            className="flex-shrink-0"
            alt="estudiante"
            bgColor="black"
            size={40}
            src={featuredReview.user.thumbnailUrl}
          />
          <div className="ml-16">
            <p className="font-subtitle3" style={{ fontSize: 18 }}>
              {featuredReview?.user.fullName || featuredReview?.user.username}
            </p>
          </div>
        </div>
        <p className="mb-16 font-body2">
          <StringParser string={featuredReview.comment} breaks>
            {({ string }) => string}
          </StringParser>
        </p>
        <div className="flex items-center">
          <p className="gray-dark font-body4">
            {humanize(featuredReview?.dateCreated)}
          </p>
          <div className="flex items-center ml-auto">
            <p
              className="mr-8 font-h4"
              style={{ fontSize: 18, lineHeight: '1em' }}
            >
              {featuredReview?.score}
            </p>
            <Star size={24} color={yellowMain} />
          </div>
        </div>
        <CrownWrapper className="absolute">
          <CrownFilled size={24} color={yellowMain} />
        </CrownWrapper>
      </div>
    </article>
  );
};

export default FeaturedReview;
